import React, { useRef, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import PaymentProofUpload from '../PaymentProofUpload';
import { PATIENT_PAYMENT_MODES, PROOF_REQUIRED_MODES } from '../../constants/payments';
import { toPaisa, fromPaisa, formatRs, formatPaisa, formatSigned } from '../../utils/money';
import { cleanDecimal } from './InvoiceEditors';

/** A fresh tender: cash, nothing typed yet. */
export const emptyTender = () => ({ mode: 'CASH', typed: null, reference: '', files: [] });

const REFERENCE_LABEL = {
  CARD: 'Card slip or approval number',
  CHEQUE: 'Cheque number',
  BANK_TRANSFER: 'Transfer reference',
  MOBILE_WALLET: 'Wallet transaction ID',
};

/**
 * The payment side of the billing counter: how the patient pays, and for cash, what they handed over
 * and what to give back.
 *
 * Controlled: `value` is owned by the counter, so what was typed and the screenshots attached survive
 * a refused payment and the bill reloading underneath. Enter in the amount box never pays; it moves
 * to the Collect button, which is what pays.
 *
 * @param {object}   props
 * @param {number}   props.total               the bill to collect, rupees (already rounded)
 * @param {number}   [props.roundingAdjustment] rupees, signed
 * @param {number}   [props.cashTotal]          what the same bill comes to settled in CASH, which
 *   is rounded down further because notes cannot make small change. Null on a bill written before
 *   cash rounding existed, and then the ordinary total stands for every mode.
 * @param {number}   [props.cashRoundingAdjustment] rupees, signed, the cash figure's own round-off
 * @param {object}   props.value                see emptyTender()
 * @param {Function} props.onChange             receives the next value
 * @param {Function} props.onCollect            (payment, files) with payment =
 *                                              { paymentMode, paymentReference, cashTendered }
 * @param {boolean}  [props.canCollect]         FINALIZE_INVOICE
 * @param {string}   [props.blockedReason]      why Collect is held back (stock short, unsaved change)
 * @param {boolean}  [props.busy]
 * @param {object}   [props.collectRef]         ref for the Collect button, for the counter's focus handling
 */
const CashTenderPanel = ({
  total,
  roundingAdjustment = 0,
  cashTotal = null,
  cashRoundingAdjustment = null,
  value,
  onChange,
  onCollect,
  canCollect = true,
  blockedReason,
  busy = false,
  collectRef,
}) => {
  const ownCollectRef = useRef(null);
  const collectButtonRef = collectRef || ownCollectRef;
  const [amountFocused, setAmountFocused] = useState(false);
  const tender = value || emptyTender();
  const set = (patch) => onChange({ ...tender, ...patch });

  const isCashMode = tender.mode === 'CASH';
  // Everything below reads these two, so the heading, the round-off line, the change and the
  // button all move together the moment the mode changes.
  const dueTotal = isCashMode && cashTotal != null ? cashTotal : total;
  const dueRounding =
    isCashMode && cashRoundingAdjustment != null ? cashRoundingAdjustment : roundingAdjustment;
  const due = toPaisa(dueTotal);
  const zero = due <= 0;
  const isCash = tender.mode === 'CASH';
  const needsProof = PROOF_REQUIRED_MODES.has(tender.mode);
  const received = tender.typed != null ? toPaisa(tender.typed) : 0;
  const change = received - due;
  const adjustment = toPaisa(dueRounding);

  let ready;
  if (zero) ready = true;
  else if (isCash) ready = received > 0 && received >= due;
  else ready = needsProof ? (tender.files || []).length > 0 : true;
  const collectEnabled = canCollect && ready && !blockedReason && (zero || !!tender.mode);

  const collect = () => {
    if (!collectEnabled || busy) return;
    if (zero) {
      onCollect({ paymentMode: null, paymentReference: null, cashTendered: null }, []);
      return;
    }
    onCollect(
      {
        paymentMode: tender.mode,
        paymentReference: isCash ? null : (tender.reference || '').trim() || null,
        cashTendered: isCash ? fromPaisa(received) : null,
      },
      needsProof ? tender.files || [] : [],
    );
  };

  // A screenshot copied from WhatsApp can be pasted straight in while the panel has focus.
  const handlePaste = (e) => {
    if (!needsProof || !e.clipboardData) return;
    const images = Array.from(e.clipboardData.files || []).filter((f) => (f.type || '').startsWith('image/'));
    if (images.length === 0) return;
    e.preventDefault();
    const stamped = images.map((f, i) => (f.name && f.name !== 'image.png'
      ? f
      : new File([f], `pasted-screenshot-${Date.now()}${i ? `-${i}` : ''}.png`, { type: f.type || 'image/png' })));
    set({ files: [...(tender.files || []), ...stamped] });
  };

  const amountDisplay = amountFocused
    ? (tender.typed ?? '')
    : (received ? formatPaisa(received) : '');

  const collectLabel = busy ? 'Collecting...' : zero ? 'Complete' : `Collect ${formatRs(dueTotal)}`;

  return (
    <section aria-label="Payment" className="bg-white rounded-2xl shadow-xl p-5 space-y-4" onPaste={handlePaste}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">To collect</span>
        <span className="text-3xl font-bold text-gray-900 tabular-nums">{formatRs(dueTotal)}</span>
      </div>
      {adjustment !== 0 && (
        <div className="flex justify-between text-sm text-gray-500 -mt-2">
          <span>Round off</span>
          <span className="tabular-nums">{formatSigned(dueRounding)}</span>
        </div>
      )}

      {!canCollect ? (
        <p className="rounded-xl bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600">
          You cannot collect payments. Ask an admin for the Finalize Invoice permission.
        </p>
      ) : zero ? (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm font-semibold text-emerald-800">
          Nothing to collect. This bill is fully discounted.
        </div>
      ) : (
        <>
          <div role="group" aria-label="Payment method" className="flex flex-wrap gap-1.5">
            {PATIENT_PAYMENT_MODES.map((m) => (
              <button
                key={m.value}
                type="button"
                aria-pressed={tender.mode === m.value}
                onClick={() => set({ mode: m.value })}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${
                  tender.mode === m.value
                    ? 'bg-emerald-600 border-emerald-600 text-white'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {isCash ? (
            <div className="space-y-3">
              {due % 100 !== 0 && (
                <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  Cash cannot settle paisa. Turn on rounding in Clinical Settings, or collect by card.
                </p>
              )}
              <div>
                <label htmlFor="cash-received" className="block text-xs font-semibold text-gray-600 mb-1">
                  Cash received <span className="text-red-500">*</span>
                </label>
                <input
                  id="cash-received"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={amountDisplay}
                  placeholder="0"
                  onFocus={() => setAmountFocused(true)}
                  onBlur={() => setAmountFocused(false)}
                  onChange={(e) => {
                    const cleaned = cleanDecimal(e.target.value);
                    set({ typed: cleaned === '' ? null : cleaned });
                  }}
                  onKeyDown={(e) => {
                    // Enter never pays. It takes the cashier to the button that does.
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    if (collectEnabled && collectButtonRef.current) collectButtonRef.current.focus();
                  }}
                  className="w-full rounded-xl border-2 border-gray-200 px-3 py-2 text-xl font-bold text-gray-900 tabular-nums focus:border-emerald-500 focus:outline-none"
                />
              </div>

              <div
                aria-live="polite"
                className={`rounded-xl px-3 py-2.5 text-sm ${
                  !received ? 'bg-gray-50 text-gray-600'
                    : received < due ? 'bg-red-50 text-red-700 font-bold'
                      : 'bg-emerald-50 text-emerald-800 font-semibold'
                }`}
              >
                {!received && 'Type the cash the patient hands over.'}
                {received > 0 && received < due && `${formatRs(fromPaisa(due - received))} short`}
                {received > 0 && received === due && 'Exact. No change to give.'}
                {received > due && (
                  <span className="flex items-baseline justify-between gap-2">
                    <span>Change to give</span>
                    <span className="text-2xl font-bold tabular-nums">{formatRs(fromPaisa(change))}</span>
                  </span>
                )}
              </div>
              {change >= 500000 && (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Change of Rs 5,000 or more. Check the amount received.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label htmlFor="payment-reference" className="block text-xs font-semibold text-gray-600 mb-1">
                  {REFERENCE_LABEL[tender.mode] || 'Reference'}
                </label>
                <input
                  id="payment-reference"
                  type="text"
                  maxLength={255}
                  autoComplete="off"
                  value={tender.reference || ''}
                  onChange={(e) => set({ reference: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              {needsProof && (
                <div>
                  <PaymentProofUpload
                    paymentMode={tender.mode}
                    files={tender.files || []}
                    onFiles={(files) => set({ files })}
                  />
                  <p className="mt-1 text-xs text-gray-400">A screenshot can also be pasted here with Ctrl+V.</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {canCollect && (
        <>
          {blockedReason && (
            <p className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> {blockedReason}
            </p>
          )}
          <button
            ref={collectButtonRef}
            type="button"
            onClick={collect}
            disabled={!collectEnabled}
            aria-disabled={busy || undefined}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-base font-bold text-white hover:bg-emerald-700 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {collectLabel}
          </button>
        </>
      )}
    </section>
  );
};

export default CashTenderPanel;
